import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container, Paper, Typography, TextField, Button,
  Grid, Box, Alert, Chip, Select, MenuItem, FormControl, InputLabel, FormControlLabel, Checkbox
} from '@mui/material';
import { api } from '../services/api';
import { auth } from '../utils/auth';
import { LATIN_AMERICAN_COUNTRIES } from '../constants/countries';
import { getProvinces, getCities } from '../constants/locations';
import WelcomeDistributor from '../assets/WelcomeDistributor2.jpg';

const SignUpDistributor = () => {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [zones, setZones] = useState([]);
  const [newZone, setNewZone] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    legalName: '',
    taxId: '',
    email: '',
    password: '',
    confirmPassword: '',
    description: '',
    logoUrl: '',
    country: 'AR',
    province: '',
    city: '',
    contactInfo: {
      phone: '',
      address: ''
    }
  });

  const predefinedZones = [
    'Todo el país',
    'GBA Norte',
    'GBA Sur',
    'GBA Oeste',
    'CABA',
    
    // Provinces
    'Provincia de Buenos Aires',
    'Provincia de Córdoba',
    'Provincia de Santa Fe',
    'Provincia de Mendoza',
    'Provincia de Tucumán',
    'Provincia de Entre Ríos',
    'Provincia de Salta',
    'Provincia de Misiones',
    'Provincia de Chaco',
    'Provincia de Corrientes',
    'Provincia de Santiago del Estero',
    'Provincia de San Juan',
    'Provincia de Jujuy',
    'Provincia de Río Negro',
    'Provincia de Neuquén',
    'Provincia de Formosa',
    'Provincia de Chubut',
    'Provincia de San Luis',
    'Provincia de Catamarca',
    'Provincia de La Rioja',
    'Provincia de La Pampa',
    'Provincia de Santa Cruz',
    'Provincia de Tierra del Fuego',

    // Important Regions
    'Costa Atlántica',
    'Cuyo',
    'NOA',
    'NEA',
    'Patagonia Norte',
    'Patagonia Sur'
  ];

  const provinces = getProvinces(formData.country);
  const cities = getCities(formData.country, formData.province);

  const validateForm = () => {
    if (!formData.name || !formData.email || !formData.password || !formData.confirmPassword) {
      setError('Por favor complete todos los campos requeridos');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Las contraseñas no coinciden');
      return false;
    }
    if (zones.length === 0) {
      setError('Debe agregar al menos una zona de distribución');
      return false;
    }
    if (!acceptTerms) {
      setError('Debe aceptar los términos y la política de privacidad');
      return false;
    }
    return true;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: { ...prev[parent], [child]: value }
      }));
    } else {
      setFormData(prev => {
        const newData = { ...prev, [name]: value };
        if (name === 'country') {
          newData.province = '';
          newData.city = '';
        }
        if (name === 'province') {
          newData.city = '';
        }
        return newData;
      });
    }
    setError('');
  };

  const handleAddZone = (selectedZone) => {
    if (selectedZone === 'Todo el país') {
      setZones(['Todo el país']);
      setNewZone('');
    } else if (selectedZone && !zones.includes(selectedZone)) {
      const newZones = zones.filter(zone => zone !== 'Todo el país');
      setZones([...newZones, selectedZone]);
      setNewZone('');
    }
  };

  const handleRemoveZone = (zoneToRemove) => {
    setZones(zones.filter(zone => zone !== zoneToRemove));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      const response = await api.registerDistributor({
        ...formData,
        zones,
        country: formData.country,
        province: formData.province,
        city: formData.city
      });
      
      // Store email for verification pending page
      localStorage.setItem('pendingVerificationEmail', formData.email);

      // Update to match login storage pattern
      auth.setCredentials(
        response.token,
        'distributor',
        response.distributor.name,
        response.distributor
      );

      // Redirect to verification pending page if verification is required
      if (response.requiresVerification) {
        navigate('/verification-pending');
      } else {
        navigate('/');
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Error al registrarse');
    }
  };

  return (
    <Container sx={{ padding: {xs:'5% 0 5% 0', md:'0px !important'}, backgroundColor:'#F4F4F4' }} component="main" maxWidth="100vw">
      <Grid container sx={{ 
        minHeight: 'calc(100vh - 106px)',
        transition: 'all 0.3s ease-in-out',
        justifyContent: {xs:'center', sm:'center', md:'end', lg:'end'},
      }}>
        <Grid item xs={12} sm={9} md={7} lg={7} sx={{
          transition: 'all 0.3s ease-in-out',
          padding: '0px !important'
        }}>
          <Box 
            sx={{ 
              px: { xs: '5%', sm: '10%', md: '20%', lg: '30%' }, 
              pt:{sm:'5%', md:'1%', lg:'1%'},
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              transition: 'all 0.3s ease-in-out'
            }}
          >
            <Typography component="h1" variant="h5" align="center" gutterBottom>
              Registrarse como vendedor
            </Typography>

            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
              <Grid container spacing={1} sx={{
              }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="Nombre Comercial"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Razón Social"
                    name="legalName"
                    value={formData.legalName}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="CUIT/RUT"
                    name="taxId"
                    value={formData.taxId}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="Contraseña"
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="Confirmar Contraseña"
                    name="confirmPassword"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  {/* <TextField
                    fullWidth
                    label="URL del Logo"
                    name="logoUrl"
                    value={formData.logoUrl}
                    onChange={handleChange}
                  /> */}
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth sx={{
                    '@media (max-width:1366px)': {
                      fontSize: '0.95rem',
                    }
                  }}>
                    <InputLabel>País</InputLabel>
                    <Select
                      sx={{
                        fontSize: '0.95rem',
                      }}
                      required
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      label="País"
                    >
                      {Object.entries(LATIN_AMERICAN_COUNTRIES).map(([code, name]) => (
                        <MenuItem key={code} value={code}>
                          {name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Provincia</InputLabel>
                    <Select
                      required
                      name="province"
                      value={formData.province}
                      onChange={handleChange}
                      label="Provincia"
                    >
                      {provinces.map((province) => (
                        <MenuItem key={province} value={province}>
                          {province}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Ciudad</InputLabel>
                    <Select
                      required
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      label="Ciudad"
                      disabled={!formData.province}
                    >
                      {cities.map((city) => (
                        <MenuItem key={city} value={city}>
                          {city}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="Teléfono"
                    name="contactInfo.phone"
                    value={formData.contactInfo.phone}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="Dirección"
                    name="contactInfo.address"
                    value={formData.contactInfo.address}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <FormControl fullWidth>
                      <InputLabel>¿A qué zonas enviás pedidos?</InputLabel>
                      <Select
                        value={newZone}
                        label="¿A qué zonas enviás pedidos?"
                        onChange={(e) => handleAddZone(e.target.value)}
                        disabled={zones.includes('Todo el país')}
                      >
                        {predefinedZones
                          .filter(zone => {
                            if (zone === 'Todo el país') {
                              return zones.length === 0;
                            }
                            return !zones.includes(zone) && !zones.includes('Todo el país');
                          })
                          .map((zone) => (
                            <MenuItem key={zone} value={zone}>
                              {zone}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {zones.map((zone, index) => (
                      <Chip
                        key={index}
                        label={zone}
                        onDelete={() => handleRemoveZone(zone)}
                      />
                    ))}
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={acceptTerms}
                        onChange={(e) => setAcceptTerms(e.target.checked)}
                      />
                    }
                    label={
                      <Typography variant="body2">
                        Acepto los <RouterLink to="/terms-and-services" target="_blank" rel="noopener noreferrer">términos</RouterLink> y la <RouterLink to="/privacy-policy" target="_blank" rel="noopener noreferrer">política de privacidad</RouterLink>.
                      </Typography>
                    }
                  />
                </Grid>
              </Grid>

              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
              >
                Continuar
              </Button>
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <RouterLink 
                  to="/pricing"
                  sx={{ cursor: 'pointer' }}
                >
                  Ver planes
                </RouterLink>
              </Box>

              <Box sx={{ textAlign: 'center' }}>
                <RouterLink 
                  to="/signup-business"
                  sx={{ cursor: 'pointer' }}
                >
                  ¿Querés comprar? Regístrate aquí
                </RouterLink>
              </Box>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={0} sm={0} md={5} lg={5} sx={{
          transition: 'all 0.1s ease-in-out',
          overflow: 'crop',
          padding: '0px !important'

        }}>
          <Box sx={{ 
            backgroundColor: '#FF6B00', 
            backgroundImage: `url(${WelcomeDistributor})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            height: '100%',
            transition: 'all 0.1s ease-in-out'
          }} />
        </Grid>
      </Grid>
    </Container>
  );
};

export default SignUpDistributor; 